<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useSession } from '@/auth/session';
import PageState from '@/components/PageState.vue';
import ProjectCard from '@/components/ProjectCard.vue';
import ProjectCreationWizard from '@/components/ProjectCreationWizard.vue';
import ProjectHealthDetails from '@/components/ProjectHealthDetails.vue';
import RecordFormDialog from '@/components/RecordFormDialog.vue';
import { usePortfolio } from '@/composables/usePortfolio';
import { eqsApi } from '@/api/eqs';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';
import type { Project } from '@/types/domain';
import {
  duplicateProjectNumbers, healthFor, isArchivedProject, isStartedProject,
  milestonesFor, overdueMilestoneCount, projectStage, summaryFor,
} from '@/utils/portfolio';

const route = useRoute();
const router = useRouter();
const session = useSession();
/** 归档 / 删除项目只给管理员——一归档就从组合、指挥台、健康度里一起消失 */
const canManageUsers = computed(() => session.canManageUsers?.value === true);

/**
 * 归档项目。
 *
 * 用的是既有的状态流转（archived），不是物理删除：数据都在，
 * 筛选切到「归档 / 删除」还能找回来，改回「进行中」就复原。
 * 测试项目该清的清掉，但不给一条不可逆的路。
 */
async function archiveProject(project: Project): Promise<void> {
  if (!await confirmAction({
    title: `把「${project.projectName}」归档？`,
    message: '归档后它不再出现在项目组合、指挥台和健康度统计里。'
      + '数据都保留——在「项目分组」里切到「归档 / 删除」还能找到，改回状态即可复原。',
    danger: true,
    confirmLabel: '归档项目',
  })) return;
  try {
    await eqsApi.advanceProjectStatus(project.id, 'archived');
    notify('success', '已归档', `${project.projectNo} ${project.projectName}`);
    await load();
  } catch (caught) {
    notify('error', '归档失败', errorMessage(caught));
  }
}
const { data, loading, error, load } = usePortfolio();
const query = ref('');
const scope = ref<'mine' | 'all'>('all');
const group = ref<'all' | 'active' | 'archived'>('all');
const health = ref<'all' | 'risk' | 'warning' | 'normal'>('all');
const plan = ref<'all' | 'planned' | 'missing'>('all');
const milestone = ref<'all' | 'overdue'>('all');
/*
  已启动 / 全部（0915）。指挥台上的异常数字只数已启动的项目，
  点「4 个红灯」进来却看到 7 行（多出来 3 个草稿），就又成了两个页面对不上。
  所以指挥台那几个链接统一带 stage=started，这一页照它筛。
  默认仍是 all——组合页本来就是项目经理来启动草稿的地方，别默认藏起来。
*/
const stage = ref<'all' | 'started' | 'not-started'>('all');
const currentPage = ref(1);
const selectedHealthProjectId = ref<Project['id'] | null>(null);
/*
 * 一页 48 个。原来是 12——18 个项目就被切成两页，第 12 个之后的项目
 * 在第一页上根本看不到，人会以为系统里没有（QHXA 就是这么「消失」的）。
 * 大屏不分页所以显示得全，两边对不上更让人困惑。
 * 项目多到 48 个以上时分页仍然生效，不会把页面拖垮。
 */
const pageSize = 48;
/**
 * 新建项目的入口。
 *
 * 原来只有「模块中心 → 项目管理」那一处，而大家日常待的是这一页，
 * 结果是从组合页能进模块页、不能反过来建项目——找不到很正常（试用反馈 8/14）。
 * 这里复用同一个向导组件，不复制一份创建逻辑。
 *
 * 只有管理员和项目经理看得见这个按钮；后端 ProjectWriteController 同样拦一道，
 * 藏按钮只是别让人白填三步表单，不是权限本身。
 */
const canCreateProjects = session.canCreateProjects;
const creatingProject = ref(false);

async function handleProjectCreated(project: Project): Promise<void> {
  creatingProject.value = false;
  await load();
  await router.push(`/projects/${project.id}/overview`);
}

const conflicts = computed(() => duplicateProjectNumbers(data.value?.projects || []));
const hasMyProjects = computed(() => Boolean(data.value?.projects.some((project) => project.isCurrentUserMember === true)));
const selectedHealthProject = computed(() => (
  data.value?.projects.find(
    (project) => String(project.id) === String(selectedHealthProjectId.value),
  )
));
/*
  除「启动状态」之外的全部筛选。

  抽出来是因为下拉里那两个读数必须**和它筛出来的行数是同一件事**
  （[[filter-dropdown-must-share-one-algorithm]]）。
  0916 第一版把读数算在「全公司非归档项目」上，于是出现：
  指挥台在管项目 15、这一页默认「我的项目」只有 14 张卡（黄统不在 NS25035 团队里），
  而下拉还写着「只看开发中（15）」——三个数字，三套口径。

  现在读数的定义是：**保持其他筛选不动，选这一档会看到几行**。
*/
function matchesExceptStage(project: Project): boolean {
  const q = query.value.trim().toLowerCase();
  const itemHealth = healthFor(data.value!, project.id)?.status;
  const summary = summaryFor(data.value!, project.id);
  const archived = isArchivedProject(project);
  return (scope.value === 'all' || project.isCurrentUserMember === true)
    && (!q || `${project.projectNo} ${project.projectName} ${project.customer || ''}`.toLowerCase().includes(q))
    && (group.value === 'all' || (group.value === 'archived' ? archived : !archived))
    && (health.value === 'all' || itemHealth === health.value)
    && (plan.value === 'all' || (plan.value === 'planned' ? Boolean(summary?.phaseCount) : !summary?.phaseCount))
    && (milestone.value === 'all'
      || overdueMilestoneCount(data.value!, project.id, data.value!.asOf) > 0);
}

const filtered = computed(() => {
  if (!data.value) return [];
  return data.value.projects.filter((project) => matchesExceptStage(project)
    && (stage.value === 'all'
      || (stage.value === 'started' ? isStartedProject(project) : !isStartedProject(project))));
});
/*
  开发中的排前面，草稿沉到最后（0916 Tony:「能否直观显示哪些是正常开发中的项目,
  哪些是草稿或者还未正式开始的项目」）。

  光加一个标签不够：草稿和在跑的项目原来是按接口顺序混着排的，一屏 12 张卡
  扫过去要一张张读标签。分成两段，"开没开工"这件事在**列表结构**上就成立了。
  已归档的再往后——它连"没开工"都不是，是已经结束的。
*/
const STAGE_ORDER: Readonly<Record<string, number>> = { started: 0, 'not-started': 1, archived: 2, deleted: 3 };
const ordered = computed(() => [...filtered.value].sort(
  (a, b) => (STAGE_ORDER[projectStage(a)] ?? 9) - (STAGE_ORDER[projectStage(b)] ?? 9)));

/** 选这一档会看到几行——和 filtered 用同一段判据，只是把 stage 那一条摘掉 */
const stageCounts = computed(() => {
  if (!data.value) return { started: 0, notStarted: 0 };
  const pool = data.value.projects.filter((project) => matchesExceptStage(project));
  return {
    started: pool.filter((project) => isStartedProject(project)).length,
    notStarted: pool.filter((project) => !isStartedProject(project)).length,
  };
});

/*
  「我的项目」藏了几个（0916 Tony:「为啥指挥台的在管项目是 15 个?」）。

  指挥台数的是**全公司**在跑的项目，这一页默认只显示你参与的——
  黄统不在 NS25035（Lucid 电镀拨钮饰条）的团队里，于是 15 变成 14。
  两个数字都没错，错在这一页从来没说过它藏了东西。
*/
const hiddenByScope = computed(() => {
  if (!data.value || scope.value !== 'mine') return 0;
  return data.value.projects.filter((project) =>
    project.isCurrentUserMember !== true
    && !isArchivedProject(project)
    && isStartedProject(project)).length;
});

const totalPages = computed(() => Math.max(1, Math.ceil(ordered.value.length / pageSize)));
const pageNumbers = computed(() => Array.from({ length: totalPages.value }, (_, index) => index + 1));
const paginated = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  return ordered.value.slice(start, start + pageSize);
});

const fromCommandCenter = computed(() =>
  health.value !== 'all' || plan.value !== 'all' || milestone.value !== 'all');

watch(data, (payload) => {
  // 从指挥台带筛选进来的，不要再被「默认只看我的项目」覆盖回去
  if (!fromCommandCenter.value) {
    scope.value = payload?.projects.some((project) => project.isCurrentUserMember === true) ? 'mine' : 'all';
  }
  currentPage.value = 1;
});
watch([query, scope, group, health, plan, milestone, stage], () => {
  currentPage.value = 1;
});

/**
 * 从指挥台点数字进来时带着筛选条件。
 *
 * 只认白名单里的值：URL 是能随手改的，收到看不懂的值要当没看见，
 * 而不是筛出一个空列表让人以为真的没项目。
 */
function applyRouteFilters(): void {
  const pick = <T extends string>(key: string, allowed: readonly T[]): T | null => {
    const raw = Array.isArray(route.query[key]) ? route.query[key]?.[0] : route.query[key];
    const value = String(raw ?? '');
    return (allowed as readonly string[]).includes(value) ? value as T : null;
  };
  const nextHealth = pick('health', ['risk', 'warning', 'normal'] as const);
  const nextPlan = pick('plan', ['planned', 'missing'] as const);
  const nextMilestone = pick('milestone', ['overdue'] as const);
  const nextScope = pick('scope', ['mine', 'all'] as const);
  const nextStage = pick('stage', ['started', 'not-started', 'all'] as const);
  if (nextHealth) health.value = nextHealth;
  if (nextPlan) plan.value = nextPlan;
  if (nextMilestone) milestone.value = nextMilestone;
  if (nextScope) scope.value = nextScope;
  if (nextStage) stage.value = nextStage;
  // 指挥台数的是全公司 12 个异常，点进来只剩我参与的 3 个会让人以为漏了
  if ((nextHealth || nextPlan || nextMilestone) && !nextScope) scope.value = 'all';
}

function clearFilters(): void {
  stage.value = 'all';
  health.value = 'all';
  plan.value = 'all';
  milestone.value = 'all';
  query.value = '';
}

watch(() => route.query, applyRouteFilters, { immediate: true, deep: true });
</script>

<template>
  <!-- 已经有数据时刷新不切成「加载中」：切了会把打开着的健康度明细拆掉重建（0930，见 refresh-must-not-remount-panel） -->
  <PageState v-if="loading && !data" state="loading" title="正在读取全部项目" message="组合列表与公司级指挥台使用同一接口。" />
  <PageState v-else-if="error" state="error" title="项目组合不可用" message="下方是接口返回的原因；重试仍失败请联系管理员。" :detail="error" @retry="load" />
  <template v-else-if="data">
    <section class="portfolio-scope" aria-label="项目范围">
      <div>
        <b>查看范围</b>
        <span>{{ hasMyProjects ? '默认显示当前账号参与的项目。' : '当前账号暂无关联项目，已显示全部项目。' }}</span>
        <!--
          藏了东西就要说出来。指挥台数全公司、这一页默认只数「我参与的」，
          不说的话两个页面的数字对不上，只能靠人来问。
        -->
        <small v-if="hiddenByScope" class="scope-hidden">
          另有 <b>{{ hiddenByScope }}</b> 个在跑的项目你不在团队里（指挥台按全公司数，所以那边会多这几个）——
          <button type="button" @click="scope = 'all'">看全部项目</button>
        </small>
      </div>
      <div class="portfolio-actions">
        <div class="scope-switch">
          <button type="button" :class="{ 'is-active': scope === 'mine' }" :disabled="!hasMyProjects" @click="scope = 'mine'">我的项目</button>
          <button type="button" :class="{ 'is-active': scope === 'all' }" @click="scope = 'all'">全部项目</button>
        </div>
        <button
          v-if="canCreateProjects"
          class="button button-primary portfolio-create"
          type="button"
          @click="creatingProject = true"
        >＋ 新建项目</button>
      </div>
    </section>
    <section class="filter-bar">
      <label class="search-field"><span>搜索</span><input v-model="query" type="search" placeholder="项目编号、名称或客户" /></label>
      <label><span>项目分组</span><select v-model="group"><option value="all">全部项目</option><option value="active">进行中</option><option value="archived">归档 / 删除</option></select></label>
      <label><span>健康度</span><select v-model="health"><option value="all">全部</option><option value="risk">红灯</option><option value="warning">黄灯</option><option value="normal">绿灯</option></select></label>
      <label><span>计划状态</span><select v-model="plan"><option value="all">全部</option><option value="planned">已配置</option><option value="missing">待补计划</option></select></label>
      <label><span>里程碑</span><select v-model="milestone"><option value="all">全部</option><option value="overdue">有逾期里程碑</option></select></label>
      <!-- 指挥台点进来时自动选成「已启动」，两边数字才对得上；这里也能手工切回全部 -->
      <label><span>启动状态</span><select v-model="stage">
        <option value="all">全部（含草稿）</option>
        <option value="started">只看开发中（{{ stageCounts.started }}）</option>
        <option value="not-started">只看草稿 / 未启动（{{ stageCounts.notStarted }}）</option>
      </select></label>
      <strong>{{ filtered.length }}<small> 项目</small></strong>
    </section>
    <p v-if="fromCommandCenter" class="filter-origin">
      已按指挥台的口径筛选，共 {{ filtered.length }} 个项目<template v-if="stage === 'started'">（只看已启动的，草稿不在其中）</template>。
      <button type="button" @click="clearFilters">看全部项目</button>
    </p>
    <aside v-if="conflicts.size" class="inline-alert">
      <b>编号冲突</b><span>{{ [...conflicts].join('、') }} 在历史数据中存在重复，内部项目 ID 保持唯一。</span>
    </aside>
    <div class="project-grid project-grid-wide">
      <ProjectCard
        v-for="project in paginated"
        :key="project.id"
        :project="project"
        :summary="summaryFor(data, project.id)"
        :health="healthFor(data, project.id)"
          :milestones="milestonesFor(data, project.id)"
        :conflict="conflicts.has(project.projectNo)"
        :can-archive="canManageUsers"
        @inspect-health="selectedHealthProjectId = project.id"
        @archive="archiveProject"
      />
    </div>
    <ProjectHealthDetails
      v-if="selectedHealthProject"
      :project="selectedHealthProject"
      :health="healthFor(data, selectedHealthProject.id)"
      :summary="summaryFor(data, selectedHealthProject.id)"
      :milestones="data.milestones"
      :projects="data.projects"
      :as-of="data.asOf"
      @close="selectedHealthProjectId = null"
      @declared="load"
    />
    <PageState v-if="!filtered.length" state="empty" title="没有符合条件的项目" message="请调整筛选条件。" />
    <nav v-else class="pagination" aria-label="项目列表分页">
      <span>第 {{ currentPage }} / {{ totalPages }} 页 · 共 {{ filtered.length }} 个结果</span>
      <div>
        <button type="button" :disabled="currentPage === 1" aria-label="上一页" @click="currentPage -= 1">上一页</button>
        <button
          v-for="page in pageNumbers"
          :key="page"
          type="button"
          :class="{ 'is-active': page === currentPage }"
          :aria-current="page === currentPage ? 'page' : undefined"
          @click="currentPage = page"
        >{{ page }}</button>
        <button type="button" :disabled="currentPage === totalPages" aria-label="下一页" @click="currentPage += 1">下一页</button>
      </div>
    </nav>

    <RecordFormDialog
      :open="creatingProject"
      title="新建项目"
      :fields="[]"
      :initial="{}"
      :submit-visible="false"
      @close="creatingProject = false"
    >
      <ProjectCreationWizard
        @cancel="creatingProject = false"
        @created="handleProjectCreated"
      />
    </RecordFormDialog>
  </template>
</template>

<style scoped>
.filter-origin {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 10px;
  padding: 8px 12px;
  border-left: 3px solid var(--blue);
  background: #EEF4FA;
  font-size: 12px;
}

.filter-origin button {
  border: 0;
  background: transparent;
  color: var(--blue);
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
}

/*
  卡片上先读到的应该是项目号 + 名称（1027）。卡片本体在 ProjectCard.vue，这一页只从外面挂样式：
  标了 .project-title / .col-name 的那一行加粗、给最小宽度，别被健康度、阶段这些次要信息挤窄。
  卡片里没标这两个类名时这条不生效。
*/
.project-grid :deep(.project-title),
.project-grid :deep(.col-name) {
  min-width: 10em;
  color: var(--ink);
  font-weight: 700;
}

.scope-hidden { display: block; margin-top: 4px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.scope-hidden b { color: var(--ink); }

.portfolio-scope {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 10px;
  padding: 14px 16px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, .85);
}

.portfolio-scope > div:first-child {
  display: grid;
  gap: 5px;
}

.portfolio-scope b {
  font-size: 13px;
}

.portfolio-scope span,
.pagination > span {
  color: var(--muted);
  font-size: 12px;
}

.scope-switch,
.pagination > div {
  display: flex;
  gap: 6px;
}

.portfolio-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

/* 与「我的项目 / 全部项目」等高，免得一排按钮大大小小 */
.portfolio-create {
  min-height: 42px;
  white-space: nowrap;
}

.scope-switch button,
.pagination button {
  min-height: 42px;
  padding: 0 13px;
  border: 1px solid var(--line);
  color: var(--ink);
  background: white;
  cursor: pointer;
  font-size: 13px;
}

.scope-switch button.is-active,
.pagination button.is-active {
  border-color: var(--blue);
  color: white;
  background: var(--blue);
}

.scope-switch button:disabled,
.pagination button:disabled {
  cursor: not-allowed;
  opacity: .45;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 18px;
}

@media (max-width: 600px) {
  .portfolio-scope,
  .pagination {
    align-items: flex-start;
    flex-direction: column;
  }

  .pagination > div {
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>
